'use client';

import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import Badge from '@/components/UI/Badge';
import DateBadge from '@/components/UI/DateBadge';
import EventDate from '@/components/UI/EventDate';
import RichText from '@/components/UI/RichText';
import { Event } from '@/types/event';
import { EventHelper } from '@/utils/helpers/EventHelper';
import { ImageHelper } from '@/utils/helpers/ImageHelper';
import Routes from '@/utils/routes';

import EventPageNotPublished from '../events/[eventId]/components/EventPageNotPublished';

interface PlacePageUpcomingSingleEventProps {
  event: Event;
  placeId: string;
}

export default function PlacePageUpcomingSingleEvent({
  event,
  placeId,
}: PlacePageUpcomingSingleEventProps) {
  const [isPublished, setIsPublished] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const coverUrl = ImageHelper.getEventCoverUrl(event);
  useEffect(() => {
    if (event) {
      setIsPublished(EventHelper.isPublished(event));
      setTimeout(() => {
        setInitialLoading(false);
      }, 100);
    }
  }, [event]);

  if (!event || initialLoading) return null;
  return (
    <Link href={Routes.PlaceEvent(placeId, event.id)}>
      <div className='mb-10 space-y-3 rounded-3xl bg-out-dark-gray px-2 py-6'>
        {coverUrl && (
          <div className='relative flex aspect-video w-full overflow-hidden rounded-3xl px-2'>
            {!isPublished && (
              <div className='bg-white/80 absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rotate-[-25deg] transform rounded-md border border-primary-filled-hover bg-black-filled-default px-4 py-2'>
                <p className='text-lg font-bold uppercase text-primary-filled-hover'>
                  Coming Soon
                </p>
              </div>
            )}
            <div className='absolute right-5 top-5 z-10'>
              <DateBadge date={event.dateFrom} />
            </div>

            {event.entryType === 'private' && (
              <div className='absolute right-3 top-3 z-10'>
                <Badge type='private' />
              </div>
            )}
            <Image
              layout='fill'
              className={clsx('object-cover', {
                'opacity-30': !isPublished,
                'opacity-80': isPublished,
              })}
              src={coverUrl}
              alt='cover'
            />
          </div>
        )}
        <p className='px-4 font-extrabold'>{event.name}</p>
        {!!event.description && (
          <RichText text={event.description} />
        )}
        <div className='flex items-center justify-between px-4'>
          <EventDate
            startDate={new Date(event.dateFrom)}
            endDate={event.dateTo ? new Date(event.dateTo) : null}
          />
        </div>
        {!isPublished && (
          <EventPageNotPublished
            publishedAt={event.publishedAt}
            setIsPublished={setIsPublished}
          />
        )}
      </div>
    </Link>
  );
}
